import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Save, User, Mail, Briefcase, Crown } from 'lucide-react';
import { CreateAprovadorData } from '../types';
import api from '../services/api';
import { useUserPermissions } from '../hooks/useUserPermissions';

const AprovadorCreate: React.FC = () => {
  const navigate = useNavigate();
  const { isMaster, isLoading: permissionsLoading } = useUserPermissions();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!permissionsLoading && !isMaster) {
      toast.error('Apenas usuários com perfil master podem cadastrar aprovadores');
      navigate('/aprovadores');
    }
  }, [isMaster, permissionsLoading, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateAprovadorData>();

  const onSubmit = async (data: CreateAprovadorData) => {
    setSaving(true);
    try {
      await api.post('/aprovadores', data);
      toast.success('Aprovador criado com sucesso!');
      navigate('/aprovadores');
    } catch (error) {
      toast.error('Erro ao criar aprovador');
      console.error('Erro:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='min-h-screen bg-gray-50 py-8'>
      <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='mb-8'>
          <div className='flex items-center space-x-4'>
            <button
              onClick={() => navigate('/aprovadores')}
              className='flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors'
            >
              <ArrowLeft className='h-5 w-5' />
              <span>Voltar</span>
            </button>
            <div>
              <h1 className='text-3xl font-bold text-gray-900'>Novo Aprovador</h1>
              <p className='text-gray-600 mt-1'>Adicione um novo aprovador ao sistema</p>
            </div>
          </div>
        </div>

        {/* Formulário */}
        <div className='bg-white rounded-lg shadow'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-900'>Informações do Aprovador</h3>
          </div>
          <div className='p-6'>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Nome */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Nome *</label>
                  <div className='relative'>
                    <User className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='text'
                      {...register('nome', { required: 'Nome é obrigatório' })}
                      className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                      placeholder='Digite o nome completo'
                    />
                  </div>
                  {errors.nome && (
                    <p className='text-red-600 text-sm mt-1'>{errors.nome.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Email *</label>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='email'
                      {...register('email', {
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido',
                        },
                      })}
                      className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                      placeholder='exemplo@empresa.com'
                    />
                  </div>
                  {errors.email && (
                    <p className='text-red-600 text-sm mt-1'>{errors.email.message}</p>
                  )}
                </div>

                {/* Cargo */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Cargo *</label>
                  <div className='relative'>
                    <Briefcase className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='text'
                      {...register('cargo', { required: 'Cargo é obrigatório' })}
                      className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                      placeholder='Ex: Gerente de TI, Coordenador de Projetos'
                    />
                  </div>
                  {errors.cargo && (
                    <p className='text-red-600 text-sm mt-1'>{errors.cargo.message}</p>
                  )}
                </div>

                {/* Status Ativo */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Status</label>
                  <div className='flex items-center space-x-3'>
                    <input
                      type='checkbox'
                      id='ativo'
                      {...register('ativo')}
                      defaultChecked={true}
                      className='h-4 w-4 text-primary-600 focus:ring-primary-500-500 border-gray-300 rounded'
                    />
                    <label htmlFor='ativo' className='text-sm text-gray-700'>
                      Aprovador ativo
                    </label>
                  </div>
                  <p className='text-xs text-gray-500 mt-1'>
                    Aprovadores inativos não podem ser selecionados em novos GMUDs
                  </p>
                </div>

                {/* Perfil Master */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Perfil Master
                  </label>
                  <div className='flex items-center space-x-3'>
                    <input
                      type='checkbox'
                      id='perfil_master'
                      {...register('perfil_master')}
                      className='h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded'
                    />
                    <label
                      htmlFor='perfil_master'
                      className='text-sm text-gray-700 flex items-center'
                    >
                      <Crown className='h-4 w-4 mr-1 text-purple-600' />
                      Perfil Master
                    </label>
                  </div>
                  <p className='text-xs text-gray-500 mt-1'>
                    Aprovadores master podem aprovar qualquer GMUD, mesmo sem serem designados como
                    aprovadores
                  </p>
                </div>
              </div>

              {/* Botões */}
              <div className='flex items-center justify-end space-x-3 pt-6 border-t border-gray-200'>
                <button
                  type='button'
                  onClick={() => navigate('/aprovadores')}
                  className='px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors'
                >
                  Cancelar
                </button>
                <button
                  type='submit'
                  disabled={saving}
                  className='flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50'
                >
                  <Save className='h-4 w-4' />
                  <span>{saving ? 'Salvando...' : 'Salvar Aprovador'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Informações Adicionais */}
        <div className='mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <svg className='h-5 w-5 text-blue-400' viewBox='0 0 20 20' fill='currentColor'>
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-blue-800'>Sobre Aprovadores</h3>
              <div className='mt-2 text-sm text-blue-700'>
                <p>
                  Os aprovadores são responsáveis por revisar e aprovar GMUDs antes da execução.
                  Eles devem ter conhecimento técnico adequado e autoridade para aprovar mudanças no
                  sistema.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AprovadorCreate;
